import { makeStyles } from '@material-ui/core'
import { useCallback, useEffect, useRef } from 'react'
import { BoundingBox } from '../../BoundingBox'
import { RegionOfInterestColors } from './Colors'
import { ScheduleType } from '../../../../../../shared/src/data/scheduleType'

const useStyles = makeStyles({
  selectionCanvas: {
    position: 'absolute',
    zIndex: 3,
  },
})

type BoundingBoxSelectorProps = {
  label: ScheduleType
  width: number
  height: number
  setOutput: (bbox: BoundingBox) => void
  isInSelectionMode: boolean
  zoom: number
}

export function BoundingBoxSelector({
  label,
  height,
  width,
  setOutput,
  isInSelectionMode,
  zoom,
}: BoundingBoxSelectorProps) {
  const classes = useStyles()
  const canvas = useRef<HTMLCanvasElement>(null)
  const context = useRef<CanvasRenderingContext2D | null>(null)
  const offsetX = useRef<number>(0)
  const offsetY = useRef<number>(0)
  const isDown = useRef<boolean>(false)
  const startX = useRef<number>(0)
  const startY = useRef<number>(0)
  const prevStartX = useRef<number>(0)
  const prevStartY = useRef<number>(0)
  const prevWidth = useRef<number>(0)
  const prevHeight = useRef<number>(0)

  const transformToBoundingBox = useCallback(
    (
      startX: number,
      startY: number,
      strokeWidth: number,
      strokeHeight: number,
    ): BoundingBox => {
      const scaledStartX = startX
      const scaledStartY = startY
      const scaledEndX = startX + strokeWidth
      const scaledEndY = startY + strokeHeight

      return new BoundingBox(
        {
          x: Math.min(scaledStartX, scaledEndX) / width,
          y: Math.min(scaledStartY, scaledEndY) / height,
        },
        {
          x: Math.max(scaledStartX, scaledEndX) / width,
          y: Math.max(scaledStartY, scaledEndY) / height,
        },
      )
    },
    [width, height],
  )

  const handleMouseDown = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const canvasOffset = canvas.current!.getBoundingClientRect()
    offsetX.current = canvasOffset.left + document.body.scrollLeft
    offsetY.current = canvasOffset.top + document.body.scrollTop

    startX.current = e.clientX - offsetX.current
    startY.current = e.clientY - offsetY.current

    isDown.current = true
  }

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      isDown.current = false
      context.current?.clearRect(0, 0, width, height)

      setOutput(
        transformToBoundingBox(
          prevStartX.current,
          prevStartY.current,
          prevWidth.current,
          prevHeight.current,
        ),
      )
    },
    [setOutput, width, height, transformToBoundingBox],
  )

  const handleMouseOut = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    isDown.current = false
  }

  const handleMouseMove = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!isDown.current) return

    const strokeWidth = e.clientX - offsetX.current - startX.current
    const strokeHeight = e.clientY - offsetY.current - startY.current

    if (canvas.current && context.current) {
      context.current.clearRect(
        0,
        0,
        canvas.current.width,
        canvas.current.height,
      )
      context.current.strokeRect(
        startX.current,
        startY.current,
        strokeWidth,
        strokeHeight,
      )
    }

    prevStartX.current = startX.current
    prevStartY.current = startY.current

    prevWidth.current = strokeWidth
    prevHeight.current = strokeHeight
  }

  useEffect(() => {
    if (!canvas.current) return

    context.current = canvas.current.getContext('2d')
  }, [])

  useEffect(() => {
    if (!canvas.current) return

    if (isInSelectionMode) {
      if (context.current) {
        context.current.globalCompositeOperation = 'source-over'
        context.current.strokeStyle = RegionOfInterestColors[label] || 'red'
        context.current.lineWidth = 3
      }
    }

    canvas.current.onmousedown = (e) => isInSelectionMode && handleMouseDown(e)
    canvas.current.onmouseup = (e) => isInSelectionMode && handleMouseUp(e)
    canvas.current.onmouseout = (e) => isInSelectionMode && handleMouseOut(e)
    canvas.current.onmousemove = (e) => isInSelectionMode && handleMouseMove(e)
  }, [handleMouseUp, isInSelectionMode, label])

  return (
    <canvas
      className={classes.selectionCanvas}
      height={height}
      width={width}
      ref={canvas}
    />
  )
}
